import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";

interface PsychometricData {
  workerId: string;
  displayName: string;
  lackOfPerseverance: number;
  negativeUrgency: number;
  positiveUrgency: number;
  lackOfPremeditation: number;
  sensationSeeking: number;
  dospert: number;
  delayDiscounting: number;
  riskScore: number;
}

interface PsychometricHeatmapProps {
  siteId?: string;
}

export const PsychometricHeatmap = ({ siteId }: PsychometricHeatmapProps) => {
  const [data, setData] = useState<PsychometricData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPsychometricData();
  }, [siteId]);

  const fetchPsychometricData = async () => {
    try {
      let query = supabase
        .from("worker_profiles")
        .select("id, display_name, psychometric_profile, current_risk_score")
        .not("psychometric_profile", "is", null);

      if (siteId) {
        query = query.eq("site_id", siteId);
      }

      const { data: workers, error } = await query;

      if (error) throw error;

      const psychData: PsychometricData[] = workers.map(w => {
        const profile = w.psychometric_profile as any;
        return {
          workerId: w.id,
          displayName: w.display_name,
          lackOfPerseverance: profile?.upps_p?.lack_of_perseverance || 0,
          negativeUrgency: profile?.upps_p?.negative_urgency || 0,
          positiveUrgency: profile?.upps_p?.positive_urgency || 0,
          lackOfPremeditation: profile?.upps_p?.lack_of_premeditation || 0,
          sensationSeeking: profile?.upps_p?.sensation_seeking || 0,
          dospert: profile?.dospert_health_safety || 0,
          delayDiscounting: profile?.delay_discounting_k || 0,
          riskScore: w.current_risk_score || 0
        };
      });

      setData(psychData.sort((a, b) => b.riskScore - a.riskScore));
    } catch (error) {
      console.error("Error fetching psychometric data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getColorForValue = (value: number) => {
    if (value >= 0.7) return "bg-destructive";
    if (value >= 0.5) return "bg-warning";
    if (value >= 0.3) return "bg-yellow-500";
    return "bg-success";
  };

  const getIntensity = (value: number) => {
    return Math.round(value * 100);
  };

  if (loading) {
    return <div className="text-center py-8">Loading psychometric data...</div>;
  }

  if (data.length === 0) {
    return (
      <Card className="p-6 text-center">
        <p className="text-muted-foreground">No psychometric assessments completed yet.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-destructive rounded" />
          <span>High Risk (≥70%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-warning rounded" />
          <span>Medium (50-69%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-success rounded" />
          <span>Low (&lt;30%)</span>
        </div>
      </div>

      {/* Heatmap Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left p-2">Worker</th>
              <th className="text-center p-2">Risk Score</th>
              <th className="text-center p-2">
                Lack of<br/>Perseverance<br/>
                <span className="text-xs font-normal text-muted-foreground">(Key Predictor)</span>
              </th>
              <th className="text-center p-2">Negative<br/>Urgency</th>
              <th className="text-center p-2">Positive<br/>Urgency</th>
              <th className="text-center p-2">Lack of<br/>Premeditation</th>
              <th className="text-center p-2">Sensation<br/>Seeking</th>
              <th className="text-center p-2">DOSPERT<br/>H/S</th>
              <th className="text-center p-2">Delay<br/>Discounting</th>
            </tr>
          </thead>
          <tbody>
            {data.map(worker => (
              <tr key={worker.workerId} className="border-b hover:bg-muted/50">
                <td className="p-2 font-medium">{worker.displayName}</td>
                <td className="text-center p-2">
                  <span className={`px-2 py-1 rounded ${
                    worker.riskScore >= 70 ? 'bg-destructive/20 text-destructive' :
                    worker.riskScore >= 40 ? 'bg-warning/20 text-warning-foreground' :
                    'bg-success/20 text-success'
                  }`}>
                    {worker.riskScore}
                  </span>
                </td>
                <td className="text-center p-2">
                  <div className={`px-2 py-1 rounded ${getColorForValue(worker.lackOfPerseverance)} text-white font-bold`}>
                    {getIntensity(worker.lackOfPerseverance)}%
                  </div>
                </td>
                <td className="text-center p-2">
                  <div className={`px-2 py-1 rounded ${getColorForValue(worker.negativeUrgency)} text-white`}>
                    {getIntensity(worker.negativeUrgency)}%
                  </div>
                </td>
                <td className="text-center p-2">
                  <div className={`px-2 py-1 rounded ${getColorForValue(worker.positiveUrgency)} text-white`}>
                    {getIntensity(worker.positiveUrgency)}%
                  </div>
                </td>
                <td className="text-center p-2">
                  <div className={`px-2 py-1 rounded ${getColorForValue(worker.lackOfPremeditation)} text-white`}>
                    {getIntensity(worker.lackOfPremeditation)}%
                  </div>
                </td>
                <td className="text-center p-2">
                  <div className={`px-2 py-1 rounded ${getColorForValue(worker.sensationSeeking)} text-white`}>
                    {getIntensity(worker.sensationSeeking)}%
                  </div>
                </td>
                <td className="text-center p-2">
                  <div className={`px-2 py-1 rounded ${getColorForValue(worker.dospert)} text-white`}>
                    {getIntensity(worker.dospert)}%
                  </div>
                </td>
                <td className="text-center p-2">
                  <div className={`px-2 py-1 rounded ${getColorForValue(worker.delayDiscounting)} text-white`}>
                    {getIntensity(worker.delayDiscounting)}%
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mt-6">
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">High Lack of Perseverance</div>
          <div className="text-2xl font-bold mt-1">
            {data.filter(w => w.lackOfPerseverance > 0.6).length}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Workers at high accident risk
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">High Negative Urgency</div>
          <div className="text-2xl font-bold mt-1">
            {data.filter(w => w.negativeUrgency > 0.6).length}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Impulsive under stress
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">High DOSPERT Risk Taking</div>
          <div className="text-2xl font-bold mt-1">
            {data.filter(w => w.dospert > 0.6).length}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Self-reported risk takers
          </div>
        </Card>
      </div>
    </div>
  );
};